import { createSelector, createSlice } from '@reduxjs/toolkit';
import moment from 'moment';

import { addMarkExtraReducers } from './asyncThunk/addMark';
import { addNewMarkExtraReducers } from './asyncThunk/addNewMark';
import { createSecretChatExtraReducers } from './asyncThunk/createSecretChat';
import { fetchChannelMessageListExtraReducers } from './asyncThunk/fetchChannelMessageList';
import { fetchChannelMessagesExtraReducers } from './asyncThunk/fetchChannelMessages';
import { fetchMessengerFiltersExtraReducers } from './asyncThunk/fetchMessengerFilters';
import { fetchPrivateMessageListExtraReducers } from './asyncThunk/fetchPrivateMessageList';
import { fetchPrivateMessagesExtraReducers } from './asyncThunk/fetchPrivateMessages';
import { fetchUnreadMessagesExtraReducers } from './asyncThunk/fetchUnreadMessages';
import { sendMessageToChannelExtraReducers } from './asyncThunk/sendMessageToChannel';
import { sendMessageToUserExtraReducers } from './asyncThunk/sendMessageToUser';

import { REQUEST } from '../../constants/request';
import {
  initialChat,
  initialMessage,
} from '../../components/Messenger/constants/initialStates';
import { POPUP_VISIBILITIES } from '../../components/Messenger/constants/popupVisibilities';

import { INITIAL_LOADING_STATE } from '../../utils/asyncThunkRequest';

export { fetchChannelMessages } from './asyncThunk/fetchChannelMessages';
export { fetchPrivateMessageList } from './asyncThunk/fetchPrivateMessageList';
export { fetchPrivateMessages } from './asyncThunk/fetchPrivateMessages';
export { fetchUnreadMessages } from './asyncThunk/fetchUnreadMessages';
export { sendMessageToChannel } from './asyncThunk/sendMessageToChannel';
export { sendMessageToUser } from './asyncThunk/sendMessageToUser';

const INITIAL_STATE = {
  chats: {
    byId: {},
    allIds: [],
  },
  conversations: {
    // [channelUID]: array of chatId;
  },
  messages: {
    byId: {},
    allIds: [],
    byChatId: {},
  },
  unreadConversations: [],
  currentRequestIds: {},
  currentLoadings: {},
  currentErrors: {},
  channels: {},
  selectedChannelUID: '',
  selectedChatId: '',
  visibilities: {
    [POPUP_VISIBILITIES.CHAT_SETTINGS]: false,
    [POPUP_VISIBILITIES.SEARCH_IN_CHAT]: false,
    [POPUP_VISIBILITIES.OPEN_MESSENGER]: false,
    [POPUP_VISIBILITIES.DELETE_CHAT]: false,
    [POPUP_VISIBILITIES.UNPINNED_CHAT]: false,
    [POPUP_VISIBILITIES.SECRET_CHAT]: false,
  },
  variantPopupMessage: '',
  attachedFile: {
    files: [],
    popup: false,
  },
  newMark: {
    isShownModal: false,
    request: {},
  },
  createSecretChat: {
    request: {},
  },
  filters: {
    active: 'all',
    list: [],
    ...INITIAL_LOADING_STATE,
  },
  pinnedMessage: {
    byId: {
      /* pinnedMessageId: null,
      isPinnedToAllMembers: false,
      userId: '', */
    },
    request: {},
  },
  sendOption: null,
  selectMessages: {
    byId: {
      /* "chatId": {
      allIds: [],
      isMessageSelect: false
      } */
    },
  },
};

export const MessengerSlice = createSlice({
  name: 'messenger',
  initialState: INITIAL_STATE,
  reducers: {
    sendSecretMessage: (state, { payload }) => {
      const messageId = Math.random() * 1e19;
      const timestamp = moment().toISOString();

      state.chats.byId[payload.chatId] = {
        ...state.chats.byId[payload.chatId],
        lastMessageSnippet: payload.message,
        timestamp,
        lastMessageId: messageId,
      };

      state.messages.byId[messageId] = {
        author: payload.userId,
        timestamp,
        chatId: payload.chatId,
        messageId,
        content: {
          message: payload.message,
        },
      };
      state.messages.allIds = [payload.messageId, ...state.messages.allIds];
      state.messages.byChatId = {
        ...state.messages.byChatId,
        [payload.chatId]: [
          messageId,
          ...(state.messages.byChatId[payload.chatId] || []),
        ],
      };
    },
    setShowNewMarkModal: (state, { payload }) => {
      state.newMark.isShownModal = payload;
    },
    selectMessengerFilter: (state, { payload }) => {
      state.filters.active = payload;
    },
    openChat: (state, { payload: chatId }) => {
      state.selectedChatId = chatId;

      const conversations =
        state.conversations?.[state.selectedChannelUID] || [];

      if (conversations.includes(chatId)) return;

      const timestamp = moment().toISOString();

      const chat = {
        ...initialChat,
        chatId,
        timestamp,
      };

      state.conversations = {
        ...state.conversations,
        [state.selectedChannelUID]: [chatId, ...conversations],
      };
      state.chats.byId[chatId] = chat;
    },
    leaveChat: state => {
      const currentChatId = state.selectedChatId;
      const filteredConversations = (
        state.conversations?.[state.selectedChannelUID] || []
      ).filter(chatId => chatId !== currentChatId);

      state.conversations = {
        ...state.conversations,
        [state.selectedChannelUID]: filteredConversations,
      };

      const chatMessages = state.messages.byChatId[currentChatId];

      chatMessages.forEach(messageId => {
        delete state.messages.byId[messageId];
      });

      state.messages.allIds = state.messages.allIds.filter(
        messageId => !chatMessages.includes(messageId),
      );
      state.chats.allIds = state.chats.allIds.filter(
        chatId => chatId !== currentChatId,
      );

      delete state.messages.byChatId[currentChatId];
      delete state.chats.byId[currentChatId];

      state.selectedChatId = filteredConversations[0] ?? '';
      state.visibilities[POPUP_VISIBILITIES.DELETE_CHAT] = false;
    },
    setSelectedChatId: (state, { payload }) => {
      state.selectedChatId = payload;
    },
    setSelectedChannelUID: (state, { payload }) => {
      state.selectedChannelUID = payload;
    },
    setChatPinned: (state, { payload }) => {
      state.chats.byId[payload.id].pinned = payload.pinned;
    },
    setChatMuted: (state, { payload }) => {
      state.chats.byId[payload.id].muted = payload.muted;
    },
    setVariantPopupMessage: (state, { payload }) => {
      state.variantPopupMessage = payload;
    },
    setAttachedFile: (state, { payload }) => {
      state.attachedFile.file = payload;
    },
    setIsShownFilePopup: (state, { payload }) => {
      state.attachedFile.popup = payload;
    },
    // TODO: This is an imitation of deleting a message, delete it when the backend API is implemented
    deleteMessage: (state, { payload }) => {
      const chanId = state.messages.byId[payload.toString()].chatId;
      delete state.messages.byId[payload.toString()];
      state.messages.allIds = state.messages.allIds.filter(
        messageId => messageId !== payload,
      );
      state.messages.byChatId[chanId] = state.messages.byChatId[chanId].filter(
        messageId => messageId !== payload,
      );
      if (state.pinnedMessage.byId[chanId]?.pinnedMessageId === payload) {
        state.pinnedMessage.byId[chanId] = {
          pinnedMessageId: null,
          isPinnedToAllMembers: false,
          userId: '',
        };
      }
    },
    // TODO: This is an imitation of pin message, remove it when the backend API is implemented
    pinMessage: (state, { payload }) => {
      state.pinnedMessage.byId[payload.chatId] = {
        pinnedMessageId: payload.messageId,
        isPinnedToAllMembers: false,
        userId: payload.userId,
      };
    },
    // TODO: This is an imitation of pin message, remove it when the backend API is implemented
    pinMessageToAllMembers: (state, { payload }) => {
      state.pinnedMessage.byId[payload.chatId] = {
        pinnedMessageId: payload.messageId,
        isPinnedToAllMembers: true,
        userId: '',
      };
    },
    // TODO: This is an imitation of unpin message, remove it when the backend API is implemented
    unpinMessage: (state, { payload }) => {
      state.pinnedMessage.byId[payload] = {
        pinnedMessageId: null,
        isPinnedToAllMembers: false,
        userId: '',
      };
    },
    setSendOption: (state, { payload }) => {
      state.sendOption = payload;
    },
    setVisibility: (state, { payload }) => {
      const { stateKey, isVisible } = payload;
      state.visibilities[stateKey] = isVisible;
    },
    setIsMassageSelect: (state, { payload }) => {
      state.selectMessages.byId[payload.chatId] = {
        ...state.selectMessages.byId[payload.chatId],
        isMessageSelect: payload.isMessageSelect,
        allIds: [],
      };
    },
    selectMessage: (state, { payload }) => {
      if (
        state.selectMessages.byId[payload.chatId].allIds.includes(
          payload.messageId,
        )
      ) {
        state.selectMessages.byId[payload.chatId].allIds = [
          ...state.selectMessages.byId[payload.chatId].allIds.filter(
            selectedId => selectedId !== payload.messageId,
          ),
        ];
      } else {
        state.selectMessages.byId[payload.chatId].allIds.push(
          payload.messageId,
        );
      }
    },
    resetMessengerSlice: () => INITIAL_STATE,
  },
  extraReducers: builder => {
    addMarkExtraReducers(builder);
    addNewMarkExtraReducers(builder);
    createSecretChatExtraReducers(builder);
    fetchChannelMessageListExtraReducers(builder);
    fetchChannelMessagesExtraReducers(builder);
    fetchMessengerFiltersExtraReducers(builder);
    fetchPrivateMessageListExtraReducers(builder);
    fetchPrivateMessagesExtraReducers(builder);
    fetchUnreadMessagesExtraReducers(builder);
    sendMessageToUserExtraReducers(builder);
    sendMessageToChannelExtraReducers(builder);
  },
});

export const {
  sendSecretMessage,
  setShowNewMarkModal,
  selectMessengerFilter,
  openChat,
  leaveChat,
  setSelectedChatId,
  setSelectedChannelUID,
  setChatPinned,
  setChatMuted,
  setVariantPopupMessage,
  setAttachedFile,
  setIsShownFilePopup,
  pinMessage,
  pinMessageToAllMembers,
  unpinMessage,
  deleteMessage,
  setSendOption,
  setVisibility,
  setIsMassageSelect,
  selectMessage,
  resetMessengerSlice,
} = MessengerSlice.actions;

export default MessengerSlice.reducer;

export const selectorVariantPopupMessage = state =>
  state.messenger.variantPopupMessage;
export const selectorSelectedMessengerChannelUID = state =>
  state.messenger.selectedChannelUID;
export const selectorSelectedChatId = state => state.messenger.selectedChatId;
export const selectorGetConversations = createSelector(
  [state => state.messenger.conversations, selectorSelectedMessengerChannelUID],
  (conversations, selectedChannelUID) =>
    conversations?.[selectedChannelUID] || [],
);
export const selectorGetConversationsLoadingStatus = state =>
  state.messenger.currentLoadings.messageList;
export const selectorGetAttachedFile = state =>
  state.messenger.attachedFile.file || null;
export const selectorIsShownFilePopup = state =>
  state.messenger.attachedFile.popup;
export const selectorVisibility = (state, key) =>
  state.messenger.visibilities[key] || false;
export const selectorGetSendOption = state => state.messenger.sendOption;
export const selectorGetChat = (state, chatId) =>
  state.messenger.chats.byId[chatId] || initialChat;
export const selectorGetMessages = state => state.messenger.messages;
export const selectorGetMessagesError = (state, chatId) =>
  state.messenger.currentErrors[chatId];
export const selectorGetMessage = (state, messageId) =>
  state.messenger.messages.byId[messageId] || initialMessage;
export const selectorMessengerLoadingStatus = (state, request) =>
  state.messenger.currentLoadings?.[request] || REQUEST.idle;
export const selectorGetMessagesByChatId = (state, chatId) =>
  state.messenger.messages.byChatId[chatId] || [];
export const selectorGetUnreadConversations = state =>
  state.messenger.unreadConversations || [];
export const selectorGetPinnedMessage = (state, chatId) =>
  state.messenger.pinnedMessage.byId[chatId] || {};
export const selectorGetIsMessageSelect = (state, chatId) =>
  state.messenger.selectMessages.byId[chatId]?.isMessageSelect || false;
export const selectorGetSelectMessages = (state, chatId) =>
  state.messenger.selectMessages.byId[chatId]?.allIds || [];
